// Retention values (spec 02 §10) read from configuration.config.gavion. SQL twin: private.retention_days
// (27_gavion_hardening_ops.sql) — same clamps, same vectors (fixtures/privacy/retention-vectors.json).
export type RetentionKey = "recordingDays" | "transcriptDays" | "auditDays";
export type RetentionSettings = Record<RetentionKey, number>;
export const RETENTION_LIMITS: Readonly<
  Record<RetentionKey, { min: number; max: number; dflt: number; path: string }>
> = Object.freeze({
  recordingDays: { min: 7, max: 3650, dflt: 90, path: "recording" },
  transcriptDays: { min: 30, max: 3650, dflt: 365, path: "transcripts" },
  auditDays: { min: 365, max: 3650, dflt: 1095, path: "audit" },
});
export const DEFAULT_RETENTION: RetentionSettings = Object.freeze({
  recordingDays: 90,
  transcriptDays: 365,
  auditDays: 1095,
});

const obj = (v: unknown): Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};

/** Non-integers (incl. numeric strings) fall back to the default; integers are clamped. */
export function clampRetention(key: RetentionKey, value: unknown): number {
  const { min, max, dflt } = RETENTION_LIMITS[key];
  if (typeof value !== "number" || !Number.isInteger(value)) return dflt;
  return Math.min(Math.max(value, min), max);
}

export function resolveRetention(gavionConfig: unknown): RetentionSettings {
  const g = obj(gavionConfig);
  const read = (key: RetentionKey) =>
    clampRetention(key, obj(g[RETENTION_LIMITS[key].path]).retention_days);
  return {
    recordingDays: read("recordingDays"),
    transcriptDays: read("transcriptDays"),
    auditDays: read("auditDays"),
  };
}

/** Settings form validation: the form refuses what the server would silently clamp. */
export function retentionError(
  key: RetentionKey,
  value: number,
): null | "not_integer" | "out_of_range" {
  if (!Number.isInteger(value)) return "not_integer";
  const { min, max } = RETENTION_LIMITS[key];
  return value < min || value > max ? "out_of_range" : null;
}
